const CUBE = "company_mind_share_metrics_monthly"

export type TrendRow = {
  [key: string]: string | number | null
}

export type TrendInsight = {
  /** The company's own brand series, when the own-brand term matches a brand. */
  userBrand: { brand: string; sharePct: number; deltaPp: number } | null
  /** The competitor whose share moved the most over the window. */
  topMover: { brand: string; deltaPp: number } | null
  /** Distinct months in the comparison window (latest + up to 3 prior). */
  monthsInWindow: number
}

/**
 * Derive the free-tier trend callout (MD-4881 step 4.2: "note trends in their
 * data relative to competitors") from raw cube rows of the share-of-search
 * total metric: dimensions brand + month, measure `searches`. Share is
 * computed here as brand searches / month total — the same derivation the
 * dashboard's own postProcess uses — rather than trusting the raw
 * `share_of_search` column, whose scale has historically been ambiguous
 * (dbt emits 0–100 while the cube description long claimed 0–1) and whose
 * monthly denominator is not even company-scoped upstream.
 *
 * The window spans up to 4 months of data: the latest month compared against
 * the month 3 earlier (or the oldest available).
 *
 * Returns null when fewer than 2 distinct months exist (a fresh report has no
 * trend yet), or when every brand present is one of the company's own terms
 * (no competitor to compare against).
 *
 * @param ownBrandTerm the RESOLVED own-brand term — callers must apply the
 *   alias-or-company-name fallback first (see `resolveOwnBrandTerm` in
 *   utils/keywords.ts); passing the raw search alias reintroduces the
 *   cleared-alias bug this contract exists to prevent.
 * @param ownBrands every term in keywords.google.brands — all are excluded
 *   from the competitor slot (stale pre-rename aliases otherwise read as
 *   collapsing "competitors").
 */
export function computeTrendInsight(
  rows: TrendRow[],
  ownBrandTerm: string | undefined,
  ownBrands: string[] = [],
): TrendInsight | null {
  const byBrand = new Map<string, Map<string, number>>()
  const monthTotals = new Map<string, number>()

  for (const row of rows) {
    const brand = row[`${CUBE}.brand`]
    const month = row[`${CUBE}.date.month`] ?? row[`${CUBE}.date`]
    const searches = Number(row[`${CUBE}.searches`] ?? 0)
    if (typeof brand !== "string" || typeof month !== "string") {
      continue
    }
    monthTotals.set(month, (monthTotals.get(month) ?? 0) + searches)
    const series = byBrand.get(brand) ?? new Map<string, number>()
    series.set(month, (series.get(month) ?? 0) + searches)
    byBrand.set(brand, series)
  }

  const orderedMonths = Array.from(monthTotals.keys()).sort()
  if (orderedMonths.length < 2) {
    return null
  }

  const baselineIndex = Math.max(0, orderedMonths.length - 4)
  const latest = orderedMonths[orderedMonths.length - 1]
  const baseline = orderedMonths[baselineIndex]
  const monthsInWindow = orderedMonths.length - baselineIndex

  const sharePct = (series: Map<string, number>, month: string): number => {
    const total = monthTotals.get(month) ?? 0
    return total > 0 ? ((series.get(month) ?? 0) / total) * 100 : 0
  }

  const normalizedTerm = ownBrandTerm?.trim().toLowerCase()
  const ownBrandTerms = new Set(
    ownBrands.map(brand => brand.trim().toLowerCase()),
  )

  let userBrand: TrendInsight["userBrand"] = null
  let topMover: TrendInsight["topMover"] = null

  for (const [brand, series] of byBrand) {
    const normalizedBrand = brand.trim().toLowerCase()
    const latestShare = sharePct(series, latest)
    const deltaPp = latestShare - sharePct(series, baseline)

    if (normalizedTerm && normalizedBrand === normalizedTerm) {
      userBrand = { brand, sharePct: latestShare, deltaPp }
      continue
    }

    if (ownBrandTerms.has(normalizedBrand)) {
      continue
    }

    if (!topMover || Math.abs(deltaPp) > Math.abs(topMover.deltaPp)) {
      topMover = { brand, deltaPp }
    }
  }

  if (!userBrand && !topMover) {
    return null
  }

  return { userBrand, topMover, monthsInWindow }
}

export const formatDeltaPp = (deltaPp: number): string =>
  `${deltaPp >= 0 ? "+" : ""}${deltaPp.toFixed(1)}pp`

export type SentenceSegment = { text: string; bold?: boolean }

/**
 * Compose the callout copy as segments (bold flags for brand names) so the
 * grammar — connectors, punctuation — lives in one exhaustively-testable
 * place instead of being scattered across conditional JSX fragments. Three
 * reachable shapes: both halves, competitor-only, own-brand-only
 * (computeTrendInsight guarantees not both null).
 */
export function formatTrendSentence(insight: TrendInsight): SentenceSegment[] {
  const { userBrand, topMover, monthsInWindow } = insight
  const segments: SentenceSegment[] = []

  if (topMover) {
    segments.push({ text: topMover.brand, bold: true })
    segments.push({
      text: ` moved ${formatDeltaPp(topMover.deltaPp)} in share of search over the last ${monthsInWindow} months`,
    })
  }
  if (topMover && userBrand) {
    segments.push({ text: ", while " })
  }
  if (userBrand) {
    segments.push({ text: userBrand.brand, bold: true })
    segments.push({
      text: ` is at ${userBrand.sharePct.toFixed(1)}% (${formatDeltaPp(userBrand.deltaPp)})`,
    })
  }
  segments.push({ text: ". Want to know what's driving it?" })

  return segments
}
